import {DiffViewSettings} from '@github-ui/diff-view-settings'
import {
  COMPACT_DIFF_LINES_NOTICE_NAME,
  DiffCompactLinesPopover,
} from '@github-ui/diff-view-settings/components/DiffCompactLinesPopover'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {ArrowUpIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {memo} from 'react'

import {useDismissNotice, useIsNoticeDismissed} from '../../hooks/use-user-notices'
import type {HeaderInfo} from '../../types/commit-types'

interface DiffsHeaderProps {
  headerInfo: HeaderInfo
  treeToggleElement: React.ReactElement
  searchBox?: React.ReactElement
  ignoreWhitespace?: boolean
}

export const DiffsHeader = memo(DiffsHeaderUnmemoized)

function DiffsHeaderUnmemoized({treeToggleElement, searchBox, ignoreWhitespace}: DiffsHeaderProps) {
  const {isSticky: isStickied, sentinelRef: diffHeaderRef} = useStickyObserver()

  return (
    <div
      className="d-flex flex-items-center gap-2 tmp-pt-3 tmp-pt-lg-4 pb-2 position-sticky top-0 color-bg-default"
      style={{zIndex: 2}}
      ref={diffHeaderRef}
    >
      <div className="d-flex flex-items-center">{treeToggleElement}</div>

      <div className="d-flex gap-2 flex-items-center width-full">
        {isStickied && <GoToTopButton />}
        {searchBox !== undefined && searchBox}
        <DiffViewSettings reloadOnWhitespaceChange defaultHideWhitespace={ignoreWhitespace} />
        <CompactDiffLinesNotice />
      </div>
    </div>
  )
}

/**
 * Wires the shared {@link DiffCompactLinesPopover} to the commits notice hooks.
 * Kept as its own component so the `compact_diff_lines` notice subscription
 * stays scoped here: dismissing the notice re-renders only this wrapper, not
 * the surrounding diffs header.
 */
function CompactDiffLinesNotice() {
  const isNoticeDismissed = useIsNoticeDismissed(COMPACT_DIFF_LINES_NOTICE_NAME)
  const {dismissNotice} = useDismissNotice(COMPACT_DIFF_LINES_NOTICE_NAME)
  return <DiffCompactLinesPopover isNoticeDismissed={isNoticeDismissed} onDismissNotice={dismissNotice} />
}

const GoToTopButton = () => {
  return (
    <Button
      leadingVisual={ArrowUpIcon}
      variant="invisible"
      className="fgColor-default px-2 f6 gap-1"
      onClick={event => {
        event.preventDefault()
        window.scrollTo({top: 0, behavior: 'smooth'})
      }}
    >
      Top
    </Button>
  )
}
